import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { PhotoPost, ReactionType } from '@/types';

export const useReaction = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({
      postId,
      reaction,
    }: {
      postId: string;
      reaction: ReactionType;
    }) => api.reactToPost(postId, reaction),

    onMutate: async ({ postId, reaction }) => {
      await queryClient.cancelQueries({
        queryKey: ['feed'],
      });

      const previous = queryClient.getQueryData<{ items: PhotoPost[] }>([
        'feed',
      ]);

      queryClient.setQueryData<{ items: PhotoPost[] }>(
        ['feed'],
        (old) => {
          if (!old) return old;

          return {
            items: old.items.map((post) => {
              if (post.id !== postId) return post;

              const reactions = {
                ...post.reactions,
              };

              if (post.userReaction) {
                reactions[post.userReaction] = Math.max(
                  0,
                  reactions[post.userReaction] - 1,
                );
              }

              reactions[reaction] += 1;

              return {
                ...post,
                userReaction: reaction,
                reactions,
                totalReactions:
                  post.totalReactions +
                  (post.userReaction ? 0 : 1),
              };
            }),
          };
        },
      );

      return { previous };
    },

    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['feed'], context.previous);
      }
    },

    onSuccess: ({ item }) => {
      queryClient.setQueryData<{ items: PhotoPost[] }>(
        ['feed'],
        (old) => {
          if (!old) return old;

          return {
            items: old.items.map((post) =>
              post.id === item.id ? item : post,
            ),
          };
        },
      );
    },
  });

  return {
    react: mutation.mutate,
    reactAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
};